/**
 * forecastReferenceInsertion.js
 *
 * MINSKY-16818 (ticket 01): "Add to document" inserts forecast citations as standard
 * `reference` marks (the Yjs-backed, margin-rail-widget, decoration-numbered citation
 * mechanism used elsewhere in the report editor) instead of the forecast-specific
 * `citationRef` mark. Where a prediction cites multiple sources at one position, all of
 * them attach as multiple `addedReferences` entries under that position's single uid.
 *
 * Kept pure/PM-schema-agnostic (mirrors buildCitationMarkAttrsList in ForecastCitations.jsx)
 * so it's unit testable without a full ProseMirror EditorState.
 */

const FORECAST_REFERENCE_SOURCE = 'forecast';

/**
 * Resolve a prediction's `citedSources` keys into the reference-data shape `addReference`
 * (yMaps/referencesYMap.js) expects, one entry per cited source, each tagged
 * `source: 'forecast'`. Uses the source's own `key` as the Yjs `localId` — forecast source
 * keys are already unique per forecast, so no position-derived id is needed.
 *
 * Unlike `buildCitationMarkAttrsList`, this doesn't take a `numberByKey` — numbering for the
 * new `reference` mark is computed by the decoration plugin from document order at render
 * time, not by this function.
 *
 * @param {string[]|null|undefined} citedSources - Cited source keys for one prediction
 * @param {Map<string, Object>} sourcesByKey - From buildForecastSourceIndex
 * @returns {Object[]} Reference-data entries ready for `addReference`
 */
export function buildForecastReferenceEntries(citedSources, sourcesByKey) {
    if (!citedSources?.length) return [];
    const entries = [];
    citedSources.forEach((key) => {
        const source = sourcesByKey.get(key);
        if (!source) return;
        entries.push({
            localId: key,
            docId: source.docId ?? null,
            fileId: source.referenceId ?? null,
            documentType: source.documentType || null,
            docTitle: source.title || null,
            content: source.snippet || null,
            segmentId: source.sentenceHit?.segmentId ?? null,
            index: source.segmentIndex ?? null,
            docSourceType: source.kind || null,
            source: FORECAST_REFERENCE_SOURCE,
        });
    });
    return entries;
}
